import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  UserPlus, 
  CheckCircle2, 
  Users, 
  Phone,
  FileText,
  Search,
  RefreshCw,
  Calendar
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const ENTRY_CATEGORIES = [
  { id: 'visitor', name: 'First-time Visitor', color: 'bg-purple-100 text-purple-700' },
  { id: 'returning_visitor', name: 'Returning Visitor', color: 'bg-blue-100 text-blue-700' },
  { id: 'member', name: 'Member (No Phone)', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'guest', name: 'Guest of Member', color: 'bg-amber-100 text-amber-700' }
];

const ManualAttendanceEntry = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    category: 'visitor',
    notes: '',
    service_date: new Date().toISOString().split('T')[0]
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  // Load today's manual entries
  const loadEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/attendance/manual-entries`, {
        params: { date: formData.service_date }
      });
      setEntries(response.data || []);
    } catch (error) {
      console.error('Error loading entries:', error);
      setEntries([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadEntries();
  }, [formData.service_date]);

  // Submit new entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      setSubmitStatus('saving');
      await axios.post(`${API}/attendance/manual-entry`, {
        ...formData,
        member_id: selectedMemberId || '',
        timestamp: new Date().toISOString()
      });
      
      setSubmitStatus('success');
      setFormData(prev => ({
        ...prev,
        full_name: '',
        phone: '',
        notes: ''
      }));
      setSelectedMemberId(null);
      loadEntries();
      
      setTimeout(() => setSubmitStatus(null), 2000);
    } catch (error) {
      console.error('Error adding entry:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 2000);
    }
  };

  const todayEntries = entries.filter(e => e.service_date === formData.service_date);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Manual Attendance Entry</h2>
            <p className="text-sm text-slate-500">Record visitors and guests who couldn't check in digitally</p>
          </div>
        </div>

        {/* Date Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Service Date</label>
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-slate-400" />
            <input
              type="date"
              className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
              value={formData.service_date}
              onChange={e => setFormData(prev => ({ ...prev, service_date: e.target.value }))}
            />
            <motion.button
              onClick={loadEntries}
              className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-5 h-5 text-slate-600" />
            </motion.button>
          </div>
        </div>

        {/* Entry Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <input
                type="text"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
                placeholder="Search member or enter visitor name..."
                value={formData.full_name}
                onChange={async (e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, full_name: val }));
                  setSelectedMemberId(null);
                  if (val.length >= 2) {
                    try {
                      const res = await axios.get(`${API}/members/search/${encodeURIComponent(val)}`);
                      setMemberSearchResults(res.data || []);
                    } catch { setMemberSearchResults([]); }
                  } else {
                    setMemberSearchResults([]);
                  }
                }}
                data-testid="attendance-name-search"
              />
              {selectedMemberId && (
                <span className="absolute right-3 top-8 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Member</span>
              )}
              {memberSearchResults.length > 0 && !selectedMemberId && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                  {memberSearchResults.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      className="w-full text-left px-4 py-2.5 hover:bg-orange-50 flex items-center justify-between border-b border-slate-50 last:border-0"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          full_name: m.full_name,
                          phone: m.phone || '',
                          category: 'member'
                        }));
                        setSelectedMemberId(m.id);
                        setMemberSearchResults([]);
                      }}
                      data-testid={`attendance-member-${m.id}`}
                    >
                      <div>
                        <span className="font-semibold text-slate-800">{m.full_name}</span>
                        {m.phone && <span className="text-xs text-slate-400 ml-2">{m.phone}</span>}
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">{m.member_number || '—'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
              <input
                type="tel"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
                placeholder="Optional"
                value={formData.phone}
                onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ENTRY_CATEGORIES.map(cat => (
                <motion.button
                  key={cat.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                  className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    formData.category === cat.id
                      ? `${cat.color} ring-2 ring-offset-2 ring-${cat.color.split('-')[1]}-400`
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {cat.name}
                </motion.button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none resize-none"
              rows={2}
              placeholder="Any additional notes about this visitor"
              value={formData.notes}
              onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          <motion.button
            type="submit"
            disabled={submitStatus === 'saving'}
            className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 ${
              submitStatus === 'success' ? 'bg-emerald-500' :
              submitStatus === 'error' ? 'bg-red-500' :
              'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
            }`}
            whileHover={{ scale: submitStatus ? 1 : 1.01 }}
            whileTap={{ scale: submitStatus ? 1 : 0.99 }}
          >
            {submitStatus === 'saving' ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : submitStatus === 'success' ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Entry Added!
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Add Entry
              </>
            )}
          </motion.button>
        </form>
      </div>

      {/* Today's Entries */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800">
            Entries for {new Date(formData.service_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h3>
          <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium">
            {todayEntries.length} {todayEntries.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
            <p className="text-slate-500">Loading entries...</p>
          </div>
        ) : todayEntries.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No manual entries for this date</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todayEntries.map((entry, index) => {
              const category = ENTRY_CATEGORIES.find(c => c.id === entry.category) || ENTRY_CATEGORIES[0];
              return (
                <motion.div
                  key={entry.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-lg font-bold text-orange-600">
                        {entry.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{entry.full_name}</p>
                      {entry.phone && (
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {entry.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${category.color}`}>
                      {category.name}
                    </span>
                    <span className="text-xs text-slate-400">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualAttendanceEntry;
